import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useMaestriaContent } from "@/shared/i18n/LocaleContext";
import styles from "./SignupModal.module.css";

/* Figma 238:560 — o "x" de 14x14 dentro do quadrado de 46 */
const CLOSE_PATH =
  "M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z";

/* Figma 235:537 — linhas do grid: [Nome, WhatsApp] / [E-mail] / [Empresa, Cargo] */
const FIELDS = [
  { name: "name", type: "text", required: true, autoComplete: "name" },
  { name: "phone", type: "tel", required: false, autoComplete: "tel" },
  { name: "email", type: "email", required: true, autoComplete: "email", wide: true },
  { name: "company", type: "text", required: true, autoComplete: "organization" },
  { name: "role", type: "text", required: true, autoComplete: "organization-title" },
] as const;

type Stage = "form" | "sending" | "success";

const SignupModalContext = createContext<() => void>(() => {});

function isMarketingAllowed(): boolean {
  const privacy = (window as Window & { auPrivacy?: { get(): { marketing: string } } }).auPrivacy;
  if (!privacy) return true;
  return privacy.get().marketing !== "denied";
}

function accentuate(text: string, accent: string) {
  const index = text.indexOf(accent);
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <strong>{accent}</strong>
      {text.slice(index + accent.length)}
    </>
  );
}

/** Abre o modal de inscrição a partir de qualquer CTA da página. */
export function useSignupModal() {
  return useContext(SignupModalContext);
}

export function SignupModalProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { signup } = useMaestriaContent();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Base do time-trap do backend: quanto tempo o formulário ficou aberto.
  const openedAtRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState<Stage>("form");
  const [failed, setFailed] = useState(false);
  const [confirmedLargeCompany, setConfirmedLargeCompany] = useState(false);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) dialog.showModal();
    else if (dialog.open) dialog.close();
    document.body.style.overflow = isOpen ? "hidden" : "";
  }, [isOpen]);

  function open() {
    setStage("form");
    setFailed(false);
    setConfirmedLargeCompany(false);
    openedAtRef.current = Date.now();
    setIsOpen(true);
    // Sinal de meio de funil para a Meta. Nao vai no carregamento da pagina:
    // o Maestria e uma landing unica, entao ViewContent no load seria copia do
    // PageView. Abrir o modal e o primeiro gesto de intencao real.
    if (isMarketingAllowed()) {
      window.fbq?.("track", "ViewContent", { content_name: "maestria-inscricao" });
    }
  }

  function closeOnBackdrop(event: MouseEvent<HTMLDialogElement>) {
    if (event.target === dialogRef.current) setIsOpen(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setStage("sending");
    setFailed(false);
    const tracking = window.auLeadTracking?.takeContactContext();
    let converted: { id?: string } | null = null;

    try {
      const response = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          company: data.get("company"),
          email: data.get("email"),
          role: data.get("role"),
          phone: data.get("phone"),
          interest: "maestria",
          page: window.location.pathname,
          source: "maestria",
          website: data.get("website"),
          elapsed_ms: Date.now() - openedAtRef.current,
          ...(tracking ? { tracking } : {}),
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      converted = await response.json().catch(() => ({}));
      form.reset();
      setStage("success");
    } catch {
      setStage("form");
      setFailed(true);
    }

    // Conversao para a Meta. Duas regras aqui:
    // 1. So depois do 200 — disparar no submit contaria lead que falhou
    //    validacao, deu erro de rede ou nem chegou no banco.
    // 2. FORA do try — erro no pixel nunca pode transformar um lead ja salvo
    //    em tela de erro (mesma regra do analytics no backend).
    // eventID = id do contato: quando a CAPI entrar, o servidor manda o MESMO
    // Lead com o MESMO id e a Meta deduplica em vez de contar duas vezes.
    if (converted && isMarketingAllowed()) {
      window.fbq?.("track", "Lead", {}, { eventID: converted.id });
    }
  }

  const qual = signup.qualification;

  return (
    <SignupModalContext.Provider value={open}>
      {children}
      <dialog
        ref={dialogRef}
        className={styles.dialog}
        aria-labelledby={titleId}
        onClose={() => setIsOpen(false)}
        onClick={closeOnBackdrop}
      >
        <div className={styles.body}>
          <button
            type="button"
            className={styles.close}
            aria-label={signup.close}
            onClick={() => setIsOpen(false)}
          >
            <svg viewBox="0 0 14 14" width={14} height={14} aria-hidden="true">
              <path d={CLOSE_PATH} />
            </svg>
          </button>

          {stage === "success" ? (
            <div className={styles.success}>
              <div className={styles.successHead}>
                <img
                  className={styles.successImage}
                  src={signup.success.image}
                  alt={signup.success.imageAlt}
                  width={224}
                  height={224}
                />
                <div className={styles.successText}>
                  <h2 id={titleId} className={styles.successTitle}>
                    {signup.success.title}
                  </h2>
                  <p className={styles.successBody}>{signup.success.description}</p>
                </div>
              </div>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setIsOpen(false)}
              >
                {signup.close}
              </button>
            </div>
          ) : (
            <form data-lead-form className={styles.form} onSubmit={handleSubmit}>
              <h2 id={titleId} className={styles.title}>
                {signup.title}
              </h2>

              <div className={styles.fields}>
                {FIELDS.map((field) => {
                  const label = signup.fields[field.name];
                  return (
                    <input
                      key={field.name}
                      className={[styles.field, "wide" in field ? styles.wide : ""]
                        .filter(Boolean)
                        .join(" ")}
                      name={field.name}
                      type={field.type}
                      required={field.required}
                      autoComplete={field.autoComplete}
                      placeholder={label}
                      aria-label={label}
                    />
                  );
                })}
              </div>

              {/* Honeypot: invisível pro usuário, preenchido só por bot. */}
              <input
                className={styles.honeypot}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <label className={styles.qualification}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  name="large_company"
                  required
                  checked={confirmedLargeCompany}
                  onChange={(event) => setConfirmedLargeCompany(event.target.checked)}
                />
                <span className={styles.qualificationCopy}>
                  <span className={styles.qualificationText}>
                    {qual ? (
                      accentuate(qual.text, qual.strong)
                    ) : (
                      <>
                        Confirmo que trabalho em uma <strong>empresa grande</strong> e estou ciente de
                        que esta vaga é exclusiva para esse perfil.
                      </>
                    )}
                  </span>
                  <em className={styles.qualificationNote}>
                    {qual ? qual.note : "As inscrições que não atenderem a esse critério poderão ser desconsideradas."}
                  </em>
                </span>
              </label>

              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.submit}
                  disabled={stage === "sending" || !confirmedLargeCompany}
                >
                  {stage === "sending" ? signup.submitting : signup.submit}
                </button>
                {failed ? (
                  <p className={styles.error} role="alert">
                    {signup.error}
                  </p>
                ) : null}
                <p className={styles.privacy}>
                  {signup.privacy.lead}
                  <a href={signup.privacy.link.href} target="_blank" rel="noreferrer">
                    {signup.privacy.link.label}
                  </a>
                  .
                </p>
              </div>
            </form>
          )}
        </div>
      </dialog>
    </SignupModalContext.Provider>
  );
}
